'use client';
import {
  type ReactNode,
  forwardRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import * as StyledSlider from './styled/slider';
import { Tooltip } from './tooltip';
import { Box, Flex } from 'styled-system/jsx';
import { Text } from './text';
import { Icon } from './icon';
import { LockerRounded } from '@ui/components/icons';

export interface SliderProps extends StyledSlider.RootProps {
  children?: ReactNode;
  marks?: {
    value: number;
    label?: ReactNode;
    id: number;
  }[];
  tooltip?: TooltipProps;
  preventValue?: number; // value to prevent
}

export const Slider = forwardRef<HTMLDivElement, SliderProps>((props, ref) => {
  const { tooltip, children, marks, preventValue, ...rootProps } = props;

  return (
    <StyledSlider.Root ref={ref} {...rootProps}>
      <StyledSlider.Context>
        {(api) => (
          <>
            {children && <StyledSlider.Label>{children}</StyledSlider.Label>}
            <StyledSlider.Control>
              <StyledSlider.Track>
                <StyledSlider.Range />
              </StyledSlider.Track>

              {api.value.map((value, index) => (
                <RenderThumb
                  index={index}
                  value={value}
                  key={index}
                  tooltip={tooltip}
                />
              ))}
            </StyledSlider.Control>
            {props.marks && (
              <StyledSlider.MarkerGroup>
                {props.marks.map((mark) => {
                  return (
                    <SliderMarker
                      key={mark.id}
                      value={mark.value}
                      label={mark.label}
                      isLocked={
                        preventValue ? mark.value > preventValue : false
                      }
                    />
                  );
                })}
              </StyledSlider.MarkerGroup>
            )}
          </>
        )}
      </StyledSlider.Context>
    </StyledSlider.Root>
  );
});

type TooltipProps = {
  gutter?: number;
  isEnabled?: boolean;
  renderContent?: (value: number) => ReactNode;
};

interface SliderMarkerProps {
  value: number;
  label?: ReactNode;
  isLocked?: boolean;
}
const SliderMarker = ({ value, label, isLocked }: SliderMarkerProps) => {
  return (
    <StyledSlider.Marker
      key={value}
      value={value}
      asChild={isLocked}
      data-cy="SliderMarker"
    >
      {isLocked ? (
        <Flex justifyContent="center" alignItems="center">
          <Icon boxSize="24px" color="black">
            <LockerRounded />
          </Icon>
          {label}
        </Flex>
      ) : (
        label
      )}
    </StyledSlider.Marker>
  );
};

const RenderThumb = ({
  index,
  value,
  tooltip,
}: {
  value: number;
  index: number;
  tooltip?: TooltipProps;
}) => {
  const [isShowTooltip, setIsShowTooltip] = useState(false);
  const { isEnabled = false } = tooltip || {};

  const handlePointerDown = useCallback(() => {
    setIsShowTooltip(true);
  }, []);

  useEffect(() => {
    if (!isEnabled) return;
    const handleDocumentPointerUp = () => setIsShowTooltip(false);
    document.addEventListener('pointerup', handleDocumentPointerUp);
    return () => {
      document.removeEventListener('pointerup', handleDocumentPointerUp);
    };
  }, [isEnabled]);

  return (
    <StyledSlider.Thumb
      key={index}
      index={index}
      display="flex"
      alignItems="center"
      onPointerDown={handlePointerDown}
    >
      {isEnabled ? (
        <SliderTooltip isOpen={isShowTooltip} value={value} {...tooltip}>
          <StyledSlider.HiddenInput defaultValue={0} />
        </SliderTooltip>
      ) : (
        <StyledSlider.HiddenInput defaultValue={0} />
      )}
    </StyledSlider.Thumb>
  );
};

const SliderTooltip = ({
  children,
  isOpen,
  value,
  ...tooltip
}: TooltipProps & {
  children: ReactNode;
  isOpen: boolean;
  value: number;
}) => {
  const { gutter = 10, renderContent = DefaultTooltipContent } = tooltip ?? {};
  return (
    <Tooltip.Root
      open={isOpen}
      positioning={{
        placement: 'top',
        gutter: gutter,
      }}
    >
      <Tooltip.Trigger>
        <Box w="var(--thumb-size)">{children}</Box>
      </Tooltip.Trigger>
      <Tooltip.Positioner>
        <Tooltip.Content>
          <Tooltip.Arrow>
            <Tooltip.ArrowTip />
          </Tooltip.Arrow>
          {renderContent(value)}
        </Tooltip.Content>
      </Tooltip.Positioner>
    </Tooltip.Root>
  );
};

export const SliderContext = StyledSlider.Context;
function DefaultTooltipContent(value: number) {
  return <Text textAlign="center">{value.toFixed(0)}%</Text>;
}
Slider.displayName = 'Slider';
